// access-request.util.ts
//
// Utility functions for safely constructing product identifiers for access requests.
// Prevents "undefined" values from appearing in Salesforce access request submissions.

/**
 * Options for building an access request title.
 * All properties are optional to handle various content types across the application.
 */
export interface AccessRequestTitleOptions {
  /** Content type (e.g., 'TechStream', 'Video', 'Seminar', 'Analysis') */
  type?: string;
  /** Category or subscription name */
  category?: string;
  /** Content title */
  title?: string;
  /** Product code (highest priority identifier) */
  productCode?: string;
  /** Channel name */
  channelName?: string;
  /** URL to use as fallback if all other properties are unavailable */
  fallbackUrl?: string;
}

/**
 * Safely constructs a product identifier for access requests, filtering out undefined/null/empty values.
 * This prevents "undefined" from appearing in Salesforce access request submissions.
 *
 * Priority order:
 * 1. productCode (if available)
 * 2. Constructed title from type, category/channelName, and title
 * 3. Fallback URL (if provided)
 * 4. 'Unknown Content' as last resort
 *
 * @param options Content properties to construct the identifier from
 * @returns A valid product identifier string without undefined values
 *
 * @example
 * // With all properties
 * buildAccessRequestTitle({
 *   type: 'TechStream',
 *   category: 'Battery',
 *   title: 'Latest Analysis',
 *   productCode: 'BAT-2024-001'
 * });
 * // Returns: 'BAT-2024-001'
 *
 * @example
 * // With some undefined properties
 * buildAccessRequestTitle({
 *   type: 'TechStream',
 *   category: undefined,
 *   title: 'Latest Analysis'
 * });
 * // Returns: 'TechStream: Latest Analysis'
 *
 * @example
 * // With all properties undefined
 * buildAccessRequestTitle({
 *   fallbackUrl: 'https://example.com/content/123'
 * });
 * // Returns: 'https://example.com/content/123'
 */
export function buildAccessRequestTitle(options: AccessRequestTitleOptions): string {
  const { type, category, title, productCode, channelName, fallbackUrl } = options;

  // Priority 1: Use productCode if available
  if (isValidString(productCode)) {
    return productCode!;
  }

  // Priority 2: Construct title from available properties
  const parts: string[] = [];

  if (isValidString(type)) {
    parts.push(type!);
  }

  // Use category or channelName (whichever is available)
  const categoryOrChannel = isValidString(category) ? category : channelName;
  if (isValidString(categoryOrChannel)) {
    parts.push(categoryOrChannel!);
  }

  if (isValidString(title)) {
    parts.push(title!);
  }

  if (parts.length > 0) {
    return parts.join(': ');
  }

  // Priority 3: Use fallback URL if provided
  if (isValidString(fallbackUrl)) {
    return fallbackUrl!;
  }

  // Priority 4: Last resort
  return 'Unknown Content';
}

/**
 * Checks if a string value is valid (not null, undefined, empty, or the string 'undefined').
 * @param value The string to check
 * @returns true if the string is valid and usable
 */
function isValidString(value: string | undefined | null): boolean {
  return value !== null &&
         value !== undefined &&
         typeof value === 'string' &&
         value.trim() !== '' &&
         value.toLowerCase() !== 'undefined';
}
